// Replaying a save made offline: apply only what this device changed since it last synced, onto the server's
// current page, so a collaborator's edits made in the meantime survive. Pure; see tests/utils/offlineMerge.test.ts.

import type { drawingPage } from 'reduxStore/projects/types'

type Keyed = { id?: string }

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

const byId = <T extends Keyed>(items: T[] | undefined) =>
  new Map((items ?? []).flatMap((item) => (item.id ? [[item.id, item] as const] : [])))

/** Server items, plus local adds and edits, minus local deletes. Where both sides edited an item, this device wins. */
export function mergeById<T extends Keyed>(base: T[] | undefined, local: T[] | undefined, server: T[] | undefined): T[] {
  const baseItems = byId(base)
  const localItems = byId(local)
  const merged = byId(server)

  for (const [id, item] of localItems) {
    const before = baseItems.get(id)
    if (!before || !same(before, item)) merged.set(id, item)
  }
  for (const id of baseItems.keys()) {
    if (!localItems.has(id)) merged.delete(id)
  }
  return [...merged.values()]
}

const MERGED_LISTS = new Set(['takeoffShapes', 'comments'])

/** The page to save: server fields this device didn't touch, this device's changed fields, merged shapes and comments. */
export function mergeOfflinePage(base: drawingPage, local: drawingPage, server: drawingPage): drawingPage {
  const merged: Record<string, unknown> = { ...server }
  const localFields = local as unknown as Record<string, unknown>
  const baseFields = base as unknown as Record<string, unknown>
  for (const key of Object.keys(localFields)) {
    if (MERGED_LISTS.has(key)) continue
    if (!same(localFields[key], baseFields[key])) merged[key] = localFields[key]
  }
  merged.takeoffShapes = mergeById(base.takeoffShapes, local.takeoffShapes, server.takeoffShapes)
  merged.comments = mergeById(base.comments, local.comments, server.comments)
  return merged as unknown as drawingPage
}
